<template>
  <div v-if="showPopup" class="modal">
    <div class="popup-content">
      <!-- Fixed header -->
      <div class="popup-header">
        <div class="header-left">
          <h3 class="role-name">{{ userRole.role_name }}</h3>
          <template v-if="!loading">
            <span class="status-dot" :class="userRole.role_is_active === '1' ? 'active' : 'inactive'"></span>
            <span class="status-text">{{ userRole.role_is_active === '1' ? 'Active' : 'Inactive' }}</span>
            
            <span class="status-dot" :class="userRole.is_distributor_level == 1 ? 'active' : 'inactive'"></span>
            <span class="status-text">{{ userRole.is_distributor_level == 1 ? 'Distributor Level' : 'Non-Distributor Level' }}</span>
          </template>
        </div>

        <button @click="$emit('close')" class="close-btn">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Scrollable body with common scrollbar -->
      <div class="popup-body">
        <div v-if="loading" class="loading-spinner">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <div v-else>
          <div class="modal-columns">
            <!-- Left: User Access -->
            <div class="left-column">
              <strong>User Access:</strong>
              <table class="user-role-table user-Role-table1">
                <thead>
                <tr>
                  <th>Module Name</th>
                  <th>Route Names</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(routes, moduleName) in groupedAccess" :key="moduleName">
                  <td>{{ moduleName }}</td>
                  <td>
                    <ul>
                      <li v-for="route in routes" :key="route">{{ route }}</li>
                    </ul>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Right: Users -->
            <div class="right-column">
              <strong>Users:</strong>
              <table class="user-role-table">
                <thead>
                <tr v-for="user in paginatedUserNames" :key="user.id">
                  <td>{{ user.name }}</td>
                </tr>
                <tr v-if="paginatedUserNames.length === 0">
                  <td colspan="1" class="text-center">No users available</td>
                </tr>
                </thead>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { viewUserRoleDetails, ViewModuleWiseUserNamesById } from "@/services/dataService";

export default {
  props: {
    showPopup: { type: Boolean, default: false },
    roleId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false,
      userRole: { role_name: '', role_is_active: '', is_distributor_level: 0 },
      groupedAccess: {},
      paginatedUserNames: [],
    };
  },
  watch: {
    showPopup(newVal) {
      if (newVal && this.roleId) {
        this.fetchData();
      }
    },
    roleId(newVal) {
      if (this.showPopup && newVal) {
        this.fetchData();
      }
    }
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token_set") || "";
        
        // Fetch role details
        const response1 = await viewUserRoleDetails(this.roleId, token);
        if (response1.data?.data) {
          const data = response1.data.data;
          this.userRole = {
            role_name: data.role_name,
            role_is_active: data.role_active || data.role_is_active,
            is_distributor_level: data.is_distributor_level
          };
          
          const filteredAccess = data.access?.filter(
            item => item.user_role_access_active === "1"
          ) || [];
          
          const grouped = {};
          filteredAccess.forEach(item => {
            if (!grouped[item.module_name]) {
              grouped[item.module_name] = [];
            }
            grouped[item.module_name].push(item.route_name);
          });
          this.groupedAccess = grouped;
        }
        
        // Fetch user names
        const response2 = await ViewModuleWiseUserNamesById(this.roleId, token);
        this.paginatedUserNames = response2.data?.data || [];
        
      } catch (error) {
        console.error("Error fetching modal data:", error);
        this.userRole = { role_name: '', role_is_active: '', is_distributor_level: 0 };
        this.groupedAccess = {};
        this.paginatedUserNames = [];
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Modal Overlay */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Modal Content */
.popup-content {
  background-color: white;
  width: 900px;
  max-width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

/* Fixed header */
.popup-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between; /* header-left on left, close button on right */
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

/* Left section for role & status */
.header-left {
  display: flex;
  align-items: center;
  gap: 8px; /* space between role name, dot, and status */
}

/* Role name styling */
.role-name {
  margin: 0;
  font-weight: bold;
  font-size: 18px;
}

/* Status Dot */
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-left: 25px;
}
.status-dot.active {
  background-color: #28a745; /* green */
}
.status-dot.inactive {
  background-color: #dc3545; /* red */
}

/* Status Text */
.status-text {
  font-size: 14px;
  color: #555;
}


/* Close Button */
.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s, color 0.2s;
}
.close-btn:hover {
  color: #B22222;
}

/* Scrollable Body with common scrollbar */
.popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* Scrollbar style */
.popup-body::-webkit-scrollbar {
  width: 8px;
}

/* Scrollbar Thumb styling */
.popup-body::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
  border-radius: 4px;
}

/* Scrollbar Thumb Hover styling */
.popup-body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0,0,0,0.4); /* darker on hover */
}

/* Columns layout */
.modal-columns {
  display: flex;
  gap: 20px;
}

/* Left & Right columns */
.left-column,
.right-column {
  flex: 1;
}

/* Loading Spinner */
.loading-spinner {
  text-align: center;
  padding: 40px 20px;
  font-size: 16px;
  color: #007bff;
}

/* Table styling */
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}
th, td {
  text-align: left;
  border: 1px solid #ddd;
  padding: 10px;
  font-size: 14px;
}
th {
  background-color: #f4f4f4;
  font-weight: 600;
}
</style>
